import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import sanitizeHtml from 'sanitize-html';
import { maxFileSizeBytes } from '@client/new/common/videos/videos.constants';
import { useAddPost } from '../api/useAddPost';
import { CommunityId } from '../model/community.types';
import {
  MAX_AUDIO_FILES,
  MAX_MEDIA_FILES,
  MAX_POST_CHARS,
} from '../model/newPost.constants';
import { analytics } from './analytics';
import { containsCommunityMention } from './channelMention';

export const isAudioFile = (file: File) => file.type.startsWith('audio');

const revokePreview = (previewUrl?: string) => {
  if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
};

export type DraftVideo = {
  uuid: string;
  previewUrl?: string;
  previewIsVideo?: boolean;
  previewTimeSec?: number;
};

export type NewPost = {
  text: string;
  setText: (text: string) => void;
  files: File[];
  videos: DraftVideo[];
  mediaCount: number;
  addFiles: (files: File[]) => void;
  removeFile: (file: File) => void;
  addVideo: (uuid: string, preview?: Omit<DraftVideo, 'uuid'>) => void;
  removeVideo: (uuid: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  submit: () => Promise<void>;
};

export const useNewPost = (
  communityId: CommunityId,
  influencerId?: string,
): NewPost => {
  const addPost = useAddPost(communityId, influencerId);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [videos, setVideos] = useState<DraftVideo[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mediaCount = files.length + videos.length;

  const addFiles = useCallback(
    (files: File[]) => {
      setFiles((current) => {
        const withinSize = files.filter(
          (file) => file.size <= maxFileSizeBytes,
        );
        if (withinSize.length !== files.length) {
          toast.error('You cannot upload files bigger than 100MB');
        }

        let audioCount = current.filter(isAudioFile).length;
        const withinAudioLimit = withinSize.filter((file) => {
          if (!isAudioFile(file)) return true;
          if (audioCount >= MAX_AUDIO_FILES) return false;
          audioCount += 1;
          return true;
        });
        if (withinAudioLimit.length < withinSize.length) {
          toast.error('You can attach only one audio file');
        }

        const remaining = MAX_MEDIA_FILES - current.length - videos.length;
        const accepted = withinAudioLimit.slice(0, Math.max(0, remaining));
        if (accepted.length < withinAudioLimit.length) {
          toast.error(`You can upload max. ${MAX_MEDIA_FILES} media files`);
        }

        return accepted.length > 0 ? [...current, ...accepted] : current;
      });
    },
    [videos.length],
  );

  const removeFile = useCallback((file: File) => {
    setFiles((current) => current.filter((item) => item !== file));
  }, []);

  const addVideo = useCallback(
    (uuid: string, preview?: Omit<DraftVideo, 'uuid'>) => {
      setVideos((current) => {
        if (current.some((video) => video.uuid === uuid)) {
          revokePreview(preview?.previewUrl);
          return current;
        }
        if (files.length + current.length >= MAX_MEDIA_FILES) {
          toast.error(`You can upload max. ${MAX_MEDIA_FILES} media files`);
          revokePreview(preview?.previewUrl);
          return current;
        }
        return [...current, { uuid, ...preview }];
      });
    },
    [files.length],
  );

  const removeVideo = useCallback((uuid: string) => {
    setVideos((current) => {
      revokePreview(current.find((video) => video.uuid === uuid)?.previewUrl);
      return current.filter((video) => video.uuid !== uuid);
    });
  }, []);

  const canSubmit = (text.trim().length > 0 || mediaCount > 0) && !isSubmitting;

  const submit = useCallback(async () => {
    if (text.length > MAX_POST_CHARS) {
      toast.error("Posts can't be longer than 4K characters.");
      return;
    }

    setIsSubmitting(true);
    const sanitizedText = sanitizeHtml(text, {
      allowedTags: ['a'],
      disallowedTagsMode: 'escape',
    });
    const created = await addPost({
      text: sanitizedText,
      files,
      videoUuids: videos.map((video) => video.uuid),
    });
    if (created) {
      analytics.trackPostCreated(files, videos.length);
      if (containsCommunityMention(text)) {
        analytics.trackBroadcastSent();
      }
      toast.success('Post added');
      setText('');
      setFiles([]);
      videos.forEach((video) => revokePreview(video.previewUrl));
      setVideos([]);
    } else {
      analytics.trackPostFailed(files, videos.length);
    }
    setIsSubmitting(false);
  }, [addPost, files, videos, text]);

  return {
    text,
    setText,
    files,
    videos,
    mediaCount,
    addFiles,
    removeFile,
    addVideo,
    removeVideo,
    canSubmit,
    isSubmitting,
    submit,
  };
};
